import { useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { CellContext } from '@tanstack/react-table';

import {
  ESwitchOptions,
  SWITCH_OPTIONS,
  TEST_STATUS_TO_COLOR_MAP
} from 'views/TestAndRelease/utils';
import Input from 'components/Input/Input';
import Button from 'components/Button/Button';
import RichText from 'components/RichText/RichText';
import { TestRules, TestStatus } from 'common/contracts';
import { EToasterStatus, showToast } from 'utils/showToast';
import { capitalizeFirstLetter } from 'utils/formattingUtil';
import DataTable from 'components/DataTableComponent/DataTable';
import useDistributionManager from 'api/useDistributionManager';
import PortalHeader from 'components/PortalHeader/PortalHeader';
import SwitchSelector from 'components/SwitchSelector/SwitchSelector';
import ActionCell from 'components/DataTableComponent/components/ActionCell';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';

export function Test() {
  const navigate = useNavigate();
  const { getVersions, createVersion } = useDistributionManager();
  const versions = getVersions.data;
  const versionOptions = useMemo(
    () =>
      versions?.map((version, index) => ({
        label: `${version.versionName}${index === 0 ? ' (latest)' : ''}`,
        value: version.versionCode
      })) || [],
    [versions]
  );
  const [selectedVersion, setSelectedVersion] = useState(versionOptions[0]);
  const [testerId, setTesterId] = useState('');
  const [newTesters, setNewTesters] = useState<string[]>([]);

  useEffect(() => {
    setSelectedVersion(versionOptions[0]);
  }, [versionOptions]);

  const handleAddTester = () => {
    if (!testerId) return;
    setNewTesters((prevTesters) => [testerId, ...prevTesters]);
    setTesterId('');
  };

  const removeTester = (playerId: string) => {
    setNewTesters((prevTesters) => prevTesters.filter((id) => id !== playerId));
  };

  if (getVersions.isLoading) {
    return <CircularProgress className="absolute top-1/2 left-1/2 h-16 w-16" />;
  }

  const selectedVersionObj = versions?.find(
    (version) => version.versionCode === selectedVersion?.value
  );
  const newTestersData = newTesters.map((id) => ({
    playerId: id,
    isPending: true
  }));
  const currentTesters =
    selectedVersionObj?.testRules?.customerIds?.map((id) => ({
      playerId: id
    })) || [];
  const allTesters = [...newTestersData, ...currentTesters];

  const onSaveClicked = () => {
    const newTestersIds = [
      ...newTesters,
      ...(selectedVersionObj?.testRules?.customerIds || [])
    ];
    createVersion.mutate(
      {
        ...selectedVersionObj,
        testRules: {
          ...((selectedVersionObj?.testRules || {}) as TestRules),
          customerIds: newTestersIds
        }
      },
      {
        onSuccess: () => {
          setNewTesters([]);
          showToast({
            message: `Version updated successfully`,
            status: EToasterStatus.SUCCESS
          });
        },
        onError: () => {
          showToast({
            message: `Failed to update version`,
            status: EToasterStatus.ERROR
          });
        }
      }
    );
  };

  const status = selectedVersionObj?.versionStatuses?.testStatus;
  const isReadyToTest = status === TestStatus.READY;
  const isNewVersionAvailable = status === TestStatus.NEW_VERSION_AVAILABLE;

  return (
    <div className="w-full mx-auto">
      <PortalHeader
        title="Test & Release"
        primaryButtonText={isReadyToTest ? 'Save & Begin Test' : 'Save'}
        primaryButtonState={{
          disabled:
            isNewVersionAvailable ||
            !selectedVersionObj ||
            createVersion.isLoading ||
            newTesters.length === 0,
          isLoading: createVersion.isLoading
        }}
        onPrimaryButtonClick={onSaveClicked}
      />
      <div className="w-full px-9 gap-3">
        <div className="w-full max-w-[220px]">
          <SwitchSelector
            options={SWITCH_OPTIONS}
            onClick={(value) => navigate(`../${value}`)}
            value={ESwitchOptions.TEST}
          />
        </div>
      </div>
      <div className="flex p-9 gap-8">
        <div className="max-w-[250px]">
          <RichText
            title="Version"
            description="Select the application version you want to test."
          />
        </div>
        <div className="flex-1">
          <div className="border border-base-sidebarBorder rounded-[20px] p-6">
            <div className="relative max-w-[320px]">
              <InputSelectDropdown
                label="Version"
                options={versionOptions}
                defaultSelected={versionOptions[0]}
                onOptionChange={(value: string) => {
                  const selectedVersion = versionOptions.find(
                    (option) => option.value === value
                  );
                  if (selectedVersion) {
                    setNewTesters([]);
                    setSelectedVersion(selectedVersion);
                  }
                }}
              />
              <div className="mt-2">
                {status && (
                  <span
                    className={`${TEST_STATUS_TO_COLOR_MAP[status]} text-sm`}
                  >
                    {capitalizeFirstLetter(status.toLowerCase())}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex pl-9 pr-9 gap-8">
        <div className="max-w-[250px]">
          <RichText
            title="Active Testers"
            description="Add testers by entering their Player ID and name"
          />
        </div>
        <div className="flex-1">
          <div className="border border-base-sidebarBorder rounded-[20px] p-6">
            <div className="mb-4">
              <div className="flex gap-2 max-w-[410px] items-end">
                <Input
                  label="Player ID Name"
                  placeholder="ID"
                  type="text"
                  name="playerId"
                  value={testerId}
                  disabled={isNewVersionAvailable || createVersion.isLoading}
                  onChange={(e) => setTesterId(e.target.value)}
                />
                <Button
                  disabled={
                    !testerId ||
                    isNewVersionAvailable ||
                    createVersion.isLoading
                  }
                  onClick={handleAddTester}
                  className="bg-base-accent text-base-accentForeground"
                  isCreateButton
                >
                  Add Player ID
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <DataTable
                columns={[
                  {
                    header: 'Player ID',
                    accessorKey: 'playerId',
                    cell: ({ row }: CellContext<any, void>) => (
                      <div className="flex gap-4">
                        <span>{row.original.playerId}</span>
                        {row.original.isPending && (
                          <span className="text-sm italic text-base-mutedForeground">
                            Pending Save
                          </span>
                        )}
                      </div>
                    )
                  },
                  {
                    accessorKey: 'actions',
                    header: '',
                    cell: ({ row }: CellContext<any, void>) => (
                      <ActionCell
                        rowData={row.original}
                        actions={[
                          {
                            label: 'Delete',
                            handler: () => removeTester(row.original.playerId),
                            disabled:
                              !row.original.isPending || isNewVersionAvailable
                          }
                        ]}
                      />
                    )
                  }
                ]}
                data={allTesters}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
